import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Shield, Loader2, AlertCircle, Check } from 'lucide-react'
import { verifyEmail } from '../api/client'

type State =
  | { status: 'verifying' }
  | { status: 'success' }
  | { status: 'error'; message: string }
  | { status: 'missing' }

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const [state, setState] = useState<State>(() => token ? { status: 'verifying' } : { status: 'missing' })

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      try {
        await verifyEmail(token)
        if (!cancelled) setState({ status: 'success' })
      } catch (err) {
        if (cancelled) return
        const msg = (err as { message?: string })?.message || 'Verification failed.'
        setState({ status: 'error', message: msg })
      }
    })()
    return () => { cancelled = true }
  }, [token])

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 -top-24 -translate-x-1/2 h-96 w-[40rem] bg-brand-500/10 blur-3xl rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="card p-8 md:p-10 space-y-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 ring-4 ring-brand-500/10 flex items-center justify-center shadow-lg">
            <Shield size={28} className="text-white" strokeWidth={2.2} />
          </div>

          {state.status === 'verifying' && (
            <>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Verifying your email</h1>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">One moment…</p>
              </div>
              <Loader2 size={28} className="animate-spin text-brand-500 mx-auto" />
            </>
          )}

          {state.status === 'success' && (
            <>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Email verified</h1>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">Your account is ready to go.</p>
              </div>
              <div className="mx-auto w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                <Check size={20} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <Link to="/dashboard" className="btn-primary w-full justify-center">Go to dashboard</Link>
            </>
          )}

          {state.status === 'error' && (
            <>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Verification failed</h1>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">The link may have expired or already been used.</p>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm text-left">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{state.message}</span>
              </div>
              <Link to="/signin" className="btn-primary w-full justify-center">Back to sign in</Link>
            </>
          )}

          {state.status === 'missing' && (
            <>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Invalid link</h1>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">This verification link is missing a token.</p>
              </div>
              <Link to="/signin" className="btn-primary w-full justify-center">Back to sign in</Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
